import styled from 'styled-components'

import {
  TileCarouselIcon,
  TileVideoIcon,
  TileReelIcon,
  TileStoryIcon,
  TileTextIcon,
  TilePendingIcon,
  TileApprovedIcon,
  TileRejectedIcon,
  VideoPlayIcon,
  NotificationIcon,
} from 'Icons'
import type { PostType, Platform } from 'Types'

import PlatformIcon from './Common/PlatformIcon'

const Wrap = styled.div<{ $status: string }>`
  background: ${({ $status: status }) => {
    return status === 'pending'
      ? '#F6F5FF'
      : status === 'approved'
        ? '#F0F8E7'
        : status === 'requestChange'
          ? '#FFECEC'
          : '#fff'
  }};
  border: 1px solid
    ${({ $status: status }) => {
      return status === 'pending'
        ? '#4F31FF'
        : status === 'approved'
          ? '#F0F8E7'
          : status === 'requestChange'
            ? '#FFECEC'
            : '#fff'
    }};
  border-radius: 6px;
  height: 58px;
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;

  &:hover {
    filter: drop-shadow(2px 2px 9px rgba(0, 0, 0, 0.15));
  }
`

const PlatformWrap = styled.div<{ $status: string }>`
  border-radius: 0 0 5px;
  position: absolute;
  z-index: 2;
  top: 1px;
  left: 2px;
  border: 3px solid
    ${({ $status: status }) => {
      return status === 'pending'
        ? '#F6F5FF'
        : status === 'approved'
          ? '#F0F8E7'
          : status === 'requestChange'
            ? '#FFECEC'
            : '#fff'
    }};
  background: ${({ $status: status }) => {
    return status === 'pending'
      ? '#F6F5FF'
      : status === 'approved'
        ? '#F0F8E7'
        : status === 'requestChange'
          ? '#FFECEC'
          : '#fff'
  }};

  svg {
    width: 18px;
    height: 18px;
  }
`

const Media = styled.div<{ $isThinMedia?: boolean }>`
  position: relative;
  width: ${({ $isThinMedia: isThinMedia }) => (isThinMedia ? '29px' : '48px;')};
  margin-left: 5px;

  > img {
    object-fit: cover;
    width: 100%;
    height: 48px;
    border-radius: 3px;
  }

  > .videoPlayIcon {
    width: ${({ $isThinMedia: isThinMedia }) =>
      isThinMedia ? '14px' : '24px;'};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  > .caption {
    background: #ffffff;
    padding: 5px;
    height: 48px;
    font-size: 6px;
    line-height: 8px;
    overflow: hidden;
    display: grid;
    white-space: normal;
    text-align: left;
    border-radius: 3px;

    > div {
      display: -webkit-box;
      -webkit-line-clamp: 5;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
`

const Time = styled.div`
  margin: 0 auto;
  font-size: 14px;
  font-weight: 700;
  line-height: 17px;
  color: #454545;
`

const TypeIconWrap = styled.div<{ $hasNotification: boolean }>`
  position: absolute;
  top: 5px;
  right: 5px;
  svg {
    width: 12px;
    height: 12px;
  }

  ${({ $hasNotification: hasNotification }) =>
    hasNotification &&
    `
      display: flex;
      align-items: center;
      gap: 5px;
  `}
`

const StatusIconWrap = styled.div`
  position: absolute;
  bottom: -5px;
  right: -5px;
  width: 22px;
`

const renderPostTypeIcon = (type: PostType) => {
  switch (type) {
    case 'carousel':
      return <TileCarouselIcon data-testid="carousel_icon" />
    case 'singleImage':
      return null
    case 'video':
      return <TileVideoIcon data-testid="video_icon" />
    case 'reel':
      return <TileReelIcon data-testid="reel_icon" />
    case 'story':
      return <TileStoryIcon data-testid="story_icon" />
    case 'text':
      return <TileTextIcon data-testid="text_icon" />
  }
}

export interface CalendarTileProps {
  platform: Platform
  status: string
  mediaSrc: string
  time: string
  type: PostType
  caption: string
  hasNotification: boolean
  isReadOnly: boolean
}

const CalendarTile: React.FC<CalendarTileProps> = ({
  platform,
  status,
  mediaSrc,
  time,
  type,
  caption,
  hasNotification,
  isReadOnly,
  ...props
}) => {
  const showVideoPlayIcon =
    type === 'video' || type === 'reel' || type === 'story'

  return (
    <Wrap $status={status} {...props}>
      <PlatformWrap $status={status}>
        <PlatformIcon platform={platform} />
      </PlatformWrap>
      {type !== 'text' ? (
        <Media $isThinMedia={type === 'reel' || type === 'story'}>
          {showVideoPlayIcon && (
            <VideoPlayIcon
              className="videoPlayIcon"
              data-testid="videoplay_icon"
            />
          )}
          <img src={mediaSrc} alt="" />
        </Media>
      ) : (
        <Media>
          <div className="caption">
            <div>{caption}</div>
          </div>
        </Media>
      )}
      <Time>{time}</Time>
      <TypeIconWrap $hasNotification={hasNotification}>
        {hasNotification && (
          <NotificationIcon data-testid="notification_icon" />
        )}
        {renderPostTypeIcon(type)}
      </TypeIconWrap>
      {!isReadOnly && (
        <StatusIconWrap data-testid="status-icon">
          {status === 'pending' && (
            <TilePendingIcon data-testid="pending_icon" />
          )}
          {status === 'approved' && (
            <TileApprovedIcon data-testid="approved_icon" />
          )}
          {status === 'requestChange' && (
            <TileRejectedIcon data-testid="rejected_icon" />
          )}
        </StatusIconWrap>
      )}
    </Wrap>
  )
}

export default CalendarTile
